import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiBookOpen, FiStar, FiHeart, FiEye, FiTrendingUp } = FiIcons;

const LearningAtmosphereSection = ({ onBooking }) => {
  const atmosphereImages = [
    {
      src: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751395134810-20240213_085721.jpg",
      title: "บรรยากาศการเรียนรู้",
      description: "ห้องเรียนที่อบอุ่น เรียนรู้ในบรรยากาศที่เป็นกันเอง"
    },
    {
      src: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751395147479-blob",
      title: "การสอนแบบใกล้ชิด",
      description: "ผู้สอนให้ความใส่ใจเป็นรายบุคคล"
    },
    {
      src: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751395156128-20240204120138_IMG_1809.JPG",
      title: "เครื่องมือการเรียน",
      description: "ใช้เครื่องมือจริงในการเรียนรู้"
    },
    {
      src: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751395126519-blob",
      title: "ปฏิบัติจริง",
      description: "ได้ฝึกปฏิบัติกับดวงชะตาของตัวเอง"
    }
  ];

  return (
    <section className="py-16 px-4 bg-gradient-to-b from-slate-800/30 to-slate-900/50 relative overflow-hidden">
      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            บรรยากาศการเรียนรู้
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            สัมผัสประสบการณ์การเรียนรู้ที่<span className="text-amber-400 font-semibold">แตกต่าง</span>
            และ<span className="text-green-400 font-semibold">มีประสิทธิภาพ</span>
          </p>
        </motion.div>

        {/* Main Learning Atmosphere Display */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-12">
          {atmosphereImages.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-gradient-to-br from-slate-800/70 to-slate-700/70 backdrop-blur-sm rounded-2xl p-6 border border-amber-500/20 hover:border-amber-500/40 transition-all duration-300 hover:transform hover:scale-105"
            >
              <div className="relative mb-6 overflow-hidden rounded-xl">
                <img 
                  src={image.src} 
                  alt={image.title}
                  className="w-full h-48 object-cover transition-transform duration-300 hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-xl font-bold text-white mb-2">
                    {image.title}
                  </h3>
                </div>
              </div>
              <p className="text-slate-300 leading-relaxed text-center">
                {image.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Learning Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-gradient-to-r from-amber-500/20 to-yellow-600/20 rounded-2xl p-8 border border-amber-500/30 mb-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-amber-400 mb-4">
                ทำไมการเรียนรู้ที่นี่จึงพิเศษ?
              </h3>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiUsers} className="text-white text-2xl" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">
                  กลุ่มเล็ก ใส่ใจรายบุคคล
                </h4>
                <p className="text-slate-300 text-sm">
                  รับเพียง 35 คน เพื่อให้สามารถดูแลและตอบคำถามได้ทั่วถึง
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiBookOpen} className="text-white text-2xl" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">
                  ปฏิบัติจริง ไม่ใช่แค่ทฤษฎี
                </h4>
                <p className="text-slate-300 text-sm">
                  ได้ฝึกวิเคราะห์ดวงชะตาของตัวเอง และเรียนรู้วิธีใช้งานจริง
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiStar} className="text-white text-2xl" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">
                  ได้เครื่องมือจริง
                </h4>
                <p className="text-slate-300 text-sm">
                  ไม่ใช่แค่ความรู้ แต่ได้ยันต์และปฏิทินที่ใช้งานได้จริง
                </p>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-xl p-6 border border-green-500/20">
              <div className="flex items-center justify-center gap-3 mb-4">
                <SafeIcon icon={FiHeart} className="text-red-400 text-2xl" />
                <h4 className="text-xl font-bold text-white">
                  ความรู้สึกของผู้เรียน
                </h4>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-green-400 font-semibold mb-2">💡 "เข้าใจง่าย"</p>
                  <p className="text-slate-300">
                    อธิบายด้วยภาษาที่เข้าใจง่าย ไม่ซับซ้อน
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-blue-400 font-semibold mb-2">🎯 "ใช้งานได้จริง"</p>
                  <p className="text-slate-300">
                    ได้ความรู้ที่นำไปประยุกต์ใช้ได้ทันที
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-purple-400 font-semibold mb-2">⚡ "คุ้มค่า"</p>
                  <p className="text-slate-300">
                    ได้มากกว่าที่คาดหวัง ทั้งความรู้และเครื่องมือ
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-amber-400 font-semibold mb-2">🌟 "ประทับใจ"</p>
                  <p className="text-slate-300">
                    ผู้สอนใส่ใจ ตอบคำถามอย่างละเอียด
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onBooking}
              className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-12 py-4 rounded-full text-xl font-bold shadow-2xl hover:shadow-amber-500/25 transition-all duration-300"
            >
              เข้าร่วมประสบการณ์นี้
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LearningAtmosphereSection;